import { useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { LogOut } from 'lucide-react';
import type { MeResponse } from '@hefesto/contracts';
import { AppShell } from '@hefesto/ui';
import { navFor } from '../lib/navigation';
import { singlePortalTarget, usePortalContext } from '../lib/portal';
import { NotificationCenterContext, useNotificationCenter } from '../lib/notifications';
import { signOut } from '../lib/session';
import { NotificationBell } from './NotificationBell';

/**
 * Shell de la sesión: el `AppShell` de `@hefesto/ui` con la navegación derivada de `/me` (y, en el
 * Portal, del contexto de la empresa en curso), la
 * campana de notificaciones (un stream por bandeja, compartido por escritorio y móvil) y el bloque
 * de usuario. El centro de notificaciones se comparte por contexto con las áreas (02A: `/admin`
 * invalida su caché con las señales en vivo).
 */
export function Shell({ me, children }: { me: MeResponse; children: ReactNode }) {
  const { pathname } = useLocation();
  // (03A) Dentro de `/t/:empresa`: navegación y campana de ESA empresa.
  const portal = usePortalContext();
  const center = useNotificationCenter(me, portal?.id);
  const nav = useMemo(() => navFor(me, portal), [me, portal]);
  return (
    <NotificationCenterContext.Provider value={center}>
      <AppShell
        brand={<Brand href={singlePortalTarget(me) ?? '/'} />}
        nav={nav}
        currentPath={pathname}
        renderLink={({ href, className, children: content, onClick, ...rest }) => (
          <Link to={href} className={className} onClick={onClick} {...rest}>
            {content}
          </Link>
        )}
        sidebarActions={<NotificationBell center={center} placement="sidebar" />}
        topbarActions={<NotificationBell center={center} placement="topbar" />}
        footer={<UserBlock me={me} />}
      >
        {children}
      </AppShell>
    </NotificationCenterContext.Provider>
  );
}

function UserBlock({ me }: { me: MeResponse }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const initials = me.user.name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  const logout = async () => {
    setBusy(true);
    await signOut().catch(() => undefined);
    navigate('/login?logout=1', { replace: true });
  };
  return (
    <div className="flex items-center gap-3 border-t border-escoria px-4 py-3">
      <span
        aria-hidden
        className="grid size-9 shrink-0 place-items-center rounded-full bg-forja font-condensed text-[13px] font-semibold text-brasa ring-1 ring-escoria-2"
      >
        {initials}
      </span>
      <div className="min-w-0 flex-1 leading-tight">
        <p className="truncate text-sm font-medium">{me.user.name}</p>
        <p className="truncate text-xs text-ceniza-2">{me.user.email}</p>
      </div>
      <button
        type="button"
        onClick={logout}
        disabled={busy}
        className="grid size-10 place-items-center rounded-md text-ceniza transition-colors hover:bg-forja hover:text-hueso disabled:opacity-50"
      >
        <LogOut className="size-4" aria-hidden />
        <span className="sr-only">Cerrar sesión</span>
      </button>
    </div>
  );
}

function Brand({ href }: { href: string }) {
  return (
    <Link to={href} className="flex items-center gap-2.5 rounded-md">
      <img src="/mots-logo.svg" alt="" className="h-6 w-auto" width={21} height={24} />
      <span className="leading-none">
        <span className="block font-display text-[15px] font-bold tracking-tight">Hefesto</span>
        <span className="mt-0.5 block text-[10.5px] text-ceniza-2">by Mot&apos;s Digital</span>
      </span>
    </Link>
  );
}
